import { Dispatch } from 'redux';

import { Client } from 'src/constants';
import { Subscription } from 'src/routes/payments/subscriptions/types/subscriptions';
import { Company } from 'src/store/clients/types';
import { arrayToMap } from 'src/utils/array';

import { Events } from '../constants/webSocketConsts/events';
import { subscriptionsApi } from '../services/api/subscriptionsApi';

import { updateListCache } from './updateListCache';

// A subscription can be created server-side after this client loaded its
// list, so a MODIFY for an unseen subscription must add it.
const listCacheOptions = { insertMissingOnModify: true };

export function handleSubscriptionUpdate(
  event: Events,
  items: Subscription[],
  dispatch: Dispatch<any>,
  companies: Company[],
  clients: Client[],
) {
  dispatch(
    subscriptionsApi.util.updateQueryData(
      'getSubscriptions',
      undefined,
      (draft?: Subscription[]) => {
        updateListCache(event, items, draft, listCacheOptions);
      },
    ),
  );

  // Updating cache for each subscription for subscription edit view
  items.forEach((item) => {
    dispatch(
      subscriptionsApi.util.updateQueryData(
        'getSubscriptionById',
        item.id,
        (draft?: Subscription) => {
          if (!draft) {
            return;
          }
          Object.assign(draft, item);
        },
      ),
    );
  });

  for (const item of items) {
    const { companyId, clientUserId } = item.fields;

    if (companyId) {
      const companiesMap = arrayToMap(companies, 'id');
      // If found, this indicates that this is a company subscription
      const company = companiesMap.get(companyId);

      dispatch(
        subscriptionsApi.util.updateQueryData(
          'getSubscriptionsByRecipientId',
          { companyId },
          (draft?: Subscription[]) => {
            updateListCache(event, items, draft, listCacheOptions);
          },
        ),
      );
      if (company) {
        // If the company is found, we need to update the subscriptions of all clients under this company
        const companyClientsIds = clients
          .filter(
            (client) =>
              // TODO:(enable_many_companies) remove the companyId check once we enable many companies
              client.fields.companyId === companyId ||
              client.fields.companyIds?.includes(companyId),
          )
          .map((client) => client.id);
        for (const clientId of companyClientsIds) {
          dispatch(
            subscriptionsApi.util.updateQueryData(
              'getSubscriptionsByRecipientId',
              { companyId, clientId },
              (draft?: Subscription[]) => {
                updateListCache(event, items, draft, listCacheOptions);
              },
            ),
          );
        }
      }
    }

    if (clientUserId) {
      // If the client is found, we need to update the subscriptions of this client
      dispatch(
        subscriptionsApi.util.updateQueryData(
          'getSubscriptionsByRecipientId',
          { clientId: clientUserId, companyId },
          (draft?: Subscription[]) => {
            updateListCache(event, items, draft, listCacheOptions);
          },
        ),
      );
    }
  }
}
